import type { StatsResponse } from '../types/moderation';

interface StatCard {
  key: keyof StatsResponse;
  label: string;
  colorClass: string;
  linkHash?: string;
}

const CARDS: StatCard[] = [
  {
    key: 'pending',
    label: 'Pending',
    colorClass: 'stat-card--amber',
    linkHash: '#/submissions?status=pending_review',
  },
  {
    key: 'approved',
    label: 'Approved',
    colorClass: 'stat-card--green',
    linkHash: '#/submissions?status=approved',
  },
  {
    key: 'rejected',
    label: 'Rejected',
    colorClass: 'stat-card--red',
    linkHash: '#/submissions?status=rejected',
  },
  {
    key: 'withdrawn',
    label: 'Withdrawn',
    colorClass: 'stat-card--grey',
    linkHash: '#/submissions?status=withdrawn',
  },
  {
    key: 'errorPipeline',
    label: 'Pipeline Errors',
    colorClass: 'stat-card--red',
    linkHash: '#/submissions?pipeline=error',
  },
  {
    key: 'thisMonth',
    label: 'This Month',
    colorClass: 'stat-card--blue',
  },
];

interface StatGridProps {
  stats: StatsResponse | null;
  loading?: boolean;
  onNavigate: (hash: string) => void;
}

/**
 * F-204 — focused stat grid extracted from Dashboard. Renders the six
 * top-level cards while loading (as skeletons, F-213 element-stable
 * variant) and after load (as buttons when they have a linkHash).
 */
export default function StatGrid({ stats, loading, onNavigate }: StatGridProps) {
  if (loading) {
    // F-213: render the same element type as the loaded variant so
    // focus order doesn't shift when data arrives.
    return (
      <div className="stat-grid" aria-busy="true" data-testid="dashboard-loading">
        {CARDS.map((c) => (
          <button
            key={c.key}
            type="button"
            disabled
            className={`stat-card stat-card--skeleton ${c.colorClass}`}
            aria-label={`Loading ${c.label}`}
          />
        ))}
      </div>
    );
  }
  if (!stats) return null;
  return (
    <div className="stat-grid">
      {CARDS.map((c) => {
        const value = stats[c.key] as number | undefined;
        const content = (
          <>
            <span className="stat-card__value">{value ?? 0}</span>
            <span className="stat-card__label">{c.label}</span>
          </>
        );
        if (c.linkHash) {
          return (
            <button
              key={c.key}
              className={`stat-card ${c.colorClass}`}
              onClick={() => onNavigate(c.linkHash!)}
              data-testid={`stat-card-${c.key}`}
            >
              {content}
            </button>
          );
        }
        return (
          <div
            key={c.key}
            className={`stat-card ${c.colorClass}`}
            data-testid={`stat-card-${c.key}`}
          >
            {content}
          </div>
        );
      })}
    </div>
  );
}
